import { useEffect, useState } from 'react';
import TextWinds from '../../common/TextWinds';
import { StudentProgress } from '../../../constants/types/student/homework/type';
import LevelProgressCard from './LevelProgressCard';

interface StudentRecitationProgressMapPanelProps {
  progress: StudentProgress | null;
}

/**
 * 개편 LMS (학생) - 클래스 상세 페이지 > 콘텐츠 판넬 > 우측 젭스온(AI인증제) [진도 지도]
 */
const StudentRecitationProgressMapPanel = ({
  progress,
}: StudentRecitationProgressMapPanelProps) => {
  const [openedLevelIds, setOpenedLevelIds] = useState<Set<number>>(
    () => new Set(),
  );

  useEffect(() => {
    if (!progress) return;

    const initiallyOpenedLevelIds = progress.recitationLevels.map(
      (level) => level.level,
    );

    setOpenedLevelIds(new Set(initiallyOpenedLevelIds));
  }, [progress]);

  if (!progress) {
    return (
      <div className="flex h-[320px] items-center justify-center rounded-xl border border-dashed border-gray4 bg-gray0 px-4 text-center">
        <TextWinds type="content_body2" color="gray6">
          진도 데이터가 없습니다.
        </TextWinds>
      </div>
    );
  }

  const handleLevelOpenToggle = (levelId: number) => {
    setOpenedLevelIds((prev) => {
      const next = new Set(prev);

      if (next.has(levelId)) {
        next.delete(levelId);
      } else {
        next.add(levelId);
      }

      return next;
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <section className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <ProgressLegend
          colorClassName="border-green-200 bg-green-100"
          label="합격"
        />
        <ProgressLegend
          colorClassName="border-red-200 bg-red-100"
          label="불합격"
        />
        <ProgressLegend
          colorClassName="border-blue-200 bg-blue-100"
          label="응시"
        />
      </section>

      {progress.recitationLevels.length === 0 ? (
        <div className="flex h-[260px] items-center justify-center rounded-xl border border-dashed border-gray4 bg-gray0 px-4 text-center">
          <TextWinds type="content_body2" color="gray6">
            진도 지도 데이터가 없습니다.
          </TextWinds>
        </div>
      ) : (
        <section className="flex flex-col gap-3">
          {progress.recitationLevels.map((level) => {
            const isOpened = openedLevelIds.has(level.level);

            return (
              <LevelProgressCard
                key={level.level}
                level={level}
                isOpened={isOpened}
                onOpenToggle={() => handleLevelOpenToggle(level.level)}
              />
            );
          })}
        </section>
      )}
    </div>
  );
};

export default StudentRecitationProgressMapPanel;

interface ProgressLegendProps {
  colorClassName: string;
  label: string;
}

const ProgressLegend = ({ colorClassName, label }: ProgressLegendProps) => {
  return (
    <div className="flex items-center gap-1.5">
      <span className={['h-4 w-4 rounded border', colorClassName].join(' ')} />

      <TextWinds type="caption1" color="gray7">
        {label}
      </TextWinds>
    </div>
  );
};
